import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Axios from "../../api-utility/axiosInstance";

// FETCH USERS
export const fetchAllUsers = createAsyncThunk(
  "users/getAll",
  async (params, { rejectWithValue }) => {
    try {
      const res = await Axios.get("/tenant/users", { params });

      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }

      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch users",
      );
    }
  },
);

// FETCH USER LIST
export const fetchUsersList = createAsyncThunk(
  "users/getUserList",
  async (params, { rejectWithValue }) => {
    try {
      const res = await Axios.get("/tenant/users/user-list", { params });

      if (res.data?.success === false) {
        return rejectWithValue(res.data.message);
      }

      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch user list",
      );
    }
  },
);

// CREATE USER
export const addUser = createAsyncThunk(
  "users/create",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await Axios.post("/tenant/users", payload);

      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }

      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

// FETCH SINGLE USER
export const getUserById = createAsyncThunk(
  "users/getById",
  async (id, { rejectWithValue }) => {
    try {
      const res = await Axios.get(`/tenant/users/${id}`);

      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }

      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.message || "Failed to fetch user",
      );
    }
  },
);

// UPDATE USER
export const updateUser = createAsyncThunk(
  "users/update",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const res = await Axios.put(`/tenant/users/${id}`, payload);

      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }

      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data);
    }
  },
);

// DELETE USER BY ID
export const deleteUser = createAsyncThunk(
  "users/delete",
  async (id, { rejectWithValue }) => {
    try {
      const response = await Axios.delete(`/tenant/users/${id}`);
      if (response.data?.success === false) {
        return rejectWithValue(response.data);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to delete user");
    }
  },
);

// CHANGE USER STATUS
export const changeUserStatus = createAsyncThunk(
  "users/changeStatus",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await Axios.post(`/tenant/users/${id}/change-status`);
      if (response.data?.success === false) {
        return rejectWithValue(response.data);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to change status");
    }
  },
);

// ADMIN: FETCH USER PROFILE
export const fetchUserProfile = createAsyncThunk(
  "users/fetchProfile",
  async (id, { rejectWithValue }) => {
    try {
      const res = await Axios.get(`/tenant/admin/users/${id}/profile`);
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data || "Failed to fetch user profile",
      );
    }
  },
);

// ADMIN: UPDATE USER PROFILE
export const updateUserProfile = createAsyncThunk(
  "users/updateProfile",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const res = await Axios.put(`/tenant/admin/users/${id}/profile`, payload);
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(
        err?.response?.data || "Failed to update user profile",
      );
    }
  }
);

// ADMIN: CHANGE USER PASSWORD
export const changeUserPassword = createAsyncThunk(
  "users/changePassword",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const res = await Axios.put(`/tenant/admin/users/${id}/change-password`, payload);
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || "Failed to change user password");
    }
  }
);

// ADMIN: FETCH USER NOTIFICATION SETTINGS
export const fetchUserNotificationSettings = createAsyncThunk(
  "users/fetchNotificationSettings",
  async (id, { rejectWithValue }) => {
    try {
      const res = await Axios.get(`/tenant/admin/users/${id}/notification-settings`);
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || "Failed to fetch notification settings");
    }
  }
);

// ADMIN: UPDATE USER NOTIFICATION SETTINGS
export const updateUserNotificationSettings = createAsyncThunk(
  "users/updateNotificationSettings",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const res = await Axios.put(`/tenant/admin/users/${id}/notification-settings`, payload);
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || "Failed to update notification settings");
    }
  }
);

// ADMIN: FETCH USER ACTIVE DEVICES
export const fetchUserActiveDevices = createAsyncThunk(
  "users/fetchActiveDevices",
  async (id, { rejectWithValue }) => {
    try {
      const res = await Axios.get(`/tenant/admin/users/${id}/active-devices`);
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || "Failed to fetch active devices");
    }
  }
);

// ADMIN: LOGOUT USER DEVICE
export const logoutUserDevice = createAsyncThunk(
  "users/logoutDevice",
  async ({ id, sessionId }, { rejectWithValue }) => {
    try {
      const res = await Axios.post(`/tenant/admin/users/${id}/logout-device/${sessionId}`);
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || "Failed to logout device");
    }
  }
);

// ADMIN: LOGOUT ALL OTHER DEVICES
export const logoutUserOtherDevices = createAsyncThunk(
  "users/logoutOtherDevices",
  async (id, { rejectWithValue }) => {
    try {
      const res = await Axios.post(`/tenant/admin/users/${id}/logout-others`);
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || "Failed to logout other devices");
    }
  }
);

// BULK DELETE USERS
export const bulkDeleteUsers = createAsyncThunk(
  "users/bulkDelete",
  async (userIds, { rejectWithValue }) => {
    try {
      const response = await Axios.delete(`/tenant/users/bulk-delete`, {
        data: { ids: userIds },
      });
      if (response.data?.success === false) {
        return rejectWithValue(response.data);
      }
      return response?.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// FETCH ALL CITIES
export const fetchAllCities = createAsyncThunk(
  "cities/getAll",
  async (params, { rejectWithValue }) => {
    try {
      // cities-data = default list (India); a country or state picked → the filtered list
      const endpoint = params?.country_id || params?.state_id ? "/tenant/cities" : "/tenant/cities-data";
      const res = await Axios.get(endpoint, { params });
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }

      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || "Failed to fetch cities");
    }
  }
);

// FETCH ALL STATES
export const fetchAllStates = createAsyncThunk(
  "states/getAll",
  async (params, { rejectWithValue }) => {
    try {
      const res = await Axios.get("/tenant/states", { params });
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || "Failed to fetch states");
    }
  }
);

// FETCH ALL COUNTRY
export const fetchAllCountry = createAsyncThunk(
  "country/getAll",
  async (params, { rejectWithValue }) => {
    try {
      const res = await Axios.get("/tenant/countries", { params });
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data?.message || "Failed to fetch countries");
    }
  }
);

// CREATE CITY
export const createCity = createAsyncThunk(
  "cities/create",
  async (payload, { rejectWithValue }) => {
    try {
      const res = await Axios.post("/tenant/cities/add-cities", payload);
      if (res.data?.success === false) {
        return rejectWithValue(res.data);
      }
      return res.data;
    } catch (err) {
      return rejectWithValue(err?.response?.data || "Failed to create city");
    }
  }
);

const usersSlice = createSlice({
  name: "users",
  initialState: {
    users: [],
    userList: [],
    cities: [],
    states: [],
    countries: [],
    total_records: 0,
    selectedUser: null,
    isLoading: false,
    error: null,
    isBulkDeleting: false,
    userProfile: null,
    userNotificationSettings: [],
    userActiveDevices: [],
    settingsLoading: false,
    settingsSaving: false,
    activeDevicesLoading: false,
  },

  extraReducers: (builder) => {
    builder
      //GET ALL USERS
      .addCase(fetchAllUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.users = action.payload.data;
        state.total_records = action.payload.total_records;
      })
      .addCase(fetchAllUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch users";
      })

      //GET ALL USERLIST
      .addCase(fetchUsersList.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUsersList.fulfilled, (state, action) => {
        state.isLoading = false;
        state.userList = action.payload.data;
        state.total_records = action.payload.total_records;
      })
      .addCase(fetchUsersList.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch users";
      })

      //GET ALL CITIES
      .addCase(fetchAllCities.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllCities.fulfilled, (state, action) => {
        state.isLoading = false;
        state.cities = action.payload.data;
      })
      .addCase(fetchAllCities.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch ciies";
      })

      //GET ALL STATES
      .addCase(fetchAllStates.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllStates.fulfilled, (state, action) => {
        state.isLoading = false;
        state.states = action.payload.data;
      })
      .addCase(fetchAllStates.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch states";
      })

      //GET ALL COUNTRY
      .addCase(fetchAllCountry.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllCountry.fulfilled, (state, action) => {
        state.isLoading = false;
        state.countries = action.payload.data;
      })
      .addCase(fetchAllCountry.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch countries";
      })

      // GET SINGLE USER
      .addCase(getUserById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getUserById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedUser = action.payload;
      })
      .addCase(getUserById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to fetch user";
      })

      // ADMIN PROFILE
      .addCase(fetchUserProfile.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedUser = action.payload.user;
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })

      // ADMIN NOTIFICATION SETTINGS
      .addCase(fetchUserNotificationSettings.pending, (state) => {
        state.settingsLoading = true;
      })
      .addCase(fetchUserNotificationSettings.fulfilled, (state, action) => {
        state.settingsLoading = false;
        state.userNotificationSettings = action.payload;
      })
      .addCase(fetchUserNotificationSettings.rejected, (state, action) => {
        state.settingsLoading = false;
        state.error = action.payload;
      })
      .addCase(updateUserNotificationSettings.pending, (state) => {
        state.settingsSaving = true;
      })
      .addCase(updateUserNotificationSettings.fulfilled, (state, action) => {
        state.settingsSaving = false;
        state.userNotificationSettings = state.userNotificationSettings.map((s) =>
          s.module_name === action.payload.module_name ? action.payload : s
        );
      })
      .addCase(updateUserNotificationSettings.rejected, (state) => {
        state.settingsSaving = false;
      })

      // ADMIN ACTIVE DEVICES
      .addCase(fetchUserActiveDevices.pending, (state) => {
        state.activeDevicesLoading = true;
      })
      .addCase(fetchUserActiveDevices.fulfilled, (state, action) => {
        state.activeDevicesLoading = false;
        state.userActiveDevices = action.payload.sessions;
      })
      .addCase(fetchUserActiveDevices.rejected, (state, action) => {
        state.activeDevicesLoading = false;
        state.error = action.payload;
      })
      .addCase(logoutUserDevice.pending, (state) => {
        state.activeDevicesLoading = true;
      })
      .addCase(logoutUserDevice.fulfilled, (state, action) => {
        state.activeDevicesLoading = false;
        state.userActiveDevices = action.payload.sessions;
      })
      .addCase(logoutUserDevice.rejected, (state) => {
        state.activeDevicesLoading = false;
      })
      .addCase(logoutUserOtherDevices.pending, (state) => {
        state.activeDevicesLoading = true;
      })
      .addCase(logoutUserOtherDevices.fulfilled, (state, action) => {
        state.activeDevicesLoading = false;
        state.userActiveDevices = action.payload.sessions;
      })
      .addCase(logoutUserOtherDevices.rejected, (state) => {
        state.activeDevicesLoading = false;
      });
  },
});

export default usersSlice.reducer;
